import React, { useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput,
  fmtDate, panelGet, useAdminGet,
} from './kit';
import { type AdminTargetIn } from './adminNav';

/**
 * Audit log — every admin action and every system change, in one place.
 *
 * Backed by admin_audit.py, which reads admin_audit_log. Three sources write
 * that table: the panel dispatcher and the raw admin routes (both through the
 * middleware in api.py, so nothing an admin does goes unrecorded), and the
 * system actors — the Razorpay webhook, the deletion sweep, stop-billing — so
 * "who did this" has an answer even when the answer is "nobody, the gateway
 * did". Everything here is read-only: there is no POST handler for this
 * section, and an audit tab that could edit the audit would not be one.
 *
 * The list is paged and sorted on the server; the bars honour the same
 * filters as the list, so clicking one narrows the other. The KPI row is
 * fixed windows over everything, whatever the filter says.
 */

type Filters = {
  q: string; admin: string; source: string; section: string; method: string;
  status: string; target: string; from: string; to: string;
};

const EMPTY: Filters = {
  q: '', admin: '', source: '', section: '', method: '', status: '', target: '', from: '', to: '',
};

const SOURCES = ['', 'panel', 'route', 'system'];
const SOURCE_LABELS: Record<string, string> = {
  '': 'Any source', panel: 'Panel action', route: 'Raw admin route', system: 'System',
};
const METHODS = ['', 'POST', 'PUT', 'PATCH', 'DELETE', 'GET'];
const STATUSES = ['', '2xx', '4xx', '5xx', 'failed'];
const STATUS_LABELS: Record<string, string> = {
  '': 'Any status', '2xx': '2xx — succeeded', '4xx': '4xx — refused', '5xx': '5xx — errored',
  failed: 'Failures (≥ 400)',
};

const COLS = ['created_at', 'admin_email', 'source', 'section', 'action', 'status', 'target',
  'duration_ms', 'summary'];
const LABELS: Record<string, string> = {
  created_at: 'When', admin_email: 'Actor', source: 'Source', section: 'Section',
  action: 'Action', status: 'Status', target: 'Target', duration_ms: 'Duration',
  summary: 'Payload',
};

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Keeps ILIKE-heavy searches off every keystroke. */
function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'mute'; children: React.ReactNode }> =
  ({ tone, children }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300';
  return <span className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

const statusPill = (s: any) => {
  if (s === null || s === undefined || s === '') return <Pill tone="mute">—</Pill>;
  const n = Number(s);
  return <Pill tone={n >= 500 ? 'bad' : n >= 400 ? 'warn' : 'ok'}>{n}</Pill>;
};

const sourcePill = (s: any) => {
  const v = String(s || 'panel');
  return <Pill tone={v === 'system' ? 'warn' : v === 'route' ? 'mute' : 'ok'}>{v}</Pill>;
};

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-grayscale-1100">{children}</p>
);

const Line: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm border-b border-grayscale-200 py-1 last:border-0">
    <span className="text-grayscale-1100 whitespace-nowrap">{label}</span>
    <span className="text-grayscale-1300 text-right break-all min-w-0">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Detail drawer — the full row, payload pretty-printed
// ---------------------------------------------------------------------------
const EntryDrawer: React.FC<{ id: number; onClose: () => void }> = ({ id, onClose }) => {
  const [entry, setEntry] = useState<any>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let alive = true;
    setEntry(null); setError('');
    panelGet('audit', 'entry', { id }).then(({ data, error }) => {
      if (!alive) return;
      setEntry(data); setError(error);
    });
    return () => { alive = false; };
  }, [id]);

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      window.prompt('Copy this:', text);
    }
  };

  const payloadText = useMemo(() => {
    if (!entry) return '';
    try { return JSON.stringify(entry.payload ?? null, null, 2); } catch { return String(entry.payload); }
  }, [entry]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full w-full max-w-xl bg-white shadow-xl flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-grayscale-300 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Audit entry #{id}</div>
            {entry && <div className="text-xs text-grayscale-1100">{fmtDate(entry.created_at)}</div>}
          </div>
          <div className="flex items-center gap-2">
            <Btn small onClick={() => copy('id', String(id))}>{copied === 'id' ? 'Copied' : 'Copy id'}</Btn>
            {payloadText && (
              <Btn small onClick={() => copy('payload', payloadText)}>
                {copied === 'payload' ? 'Copied' : 'Copy JSON'}
              </Btn>
            )}
            <Btn small onClick={onClose}>Close</Btn>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {error && <Msg kind="error">{error}</Msg>}
          {!entry && !error && <Loading />}
          {entry && (
            <>
              <div>
                <Line label="Actor" value={entry.admin_email} />
                <Line label="Source" value={sourcePill(entry.source)} />
                <Line label="Section / action" value={`${entry.section} / ${entry.action}`} />
                <Line label="Route" value={entry.method && entry.path ? `${entry.method} ${entry.path}` : ''} />
                <Line label="Status" value={statusPill(entry.status)} />
                <Line label="Duration" value={entry.duration_ms != null ? `${entry.duration_ms} ms` : ''} />
                <Line label="IP" value={entry.ip} />
                <Line label="Target user id" value={entry.target_user_id} />
                <Line label="Target email" value={entry.target_email} />
                <Line label="Request id" value={entry.request_id} />
              </div>
              <div>
                <div className="text-xs font-bold text-grayscale-1200 mb-1">Payload (secrets redacted, capped at 4 KB)</div>
                <pre className="text-xs bg-grayscale-100 border border-grayscale-300 rounded-lg p-3 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">
                  {payloadText || 'null'}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------
/**
 * Deep link (owner, 2026-10-05): /admin?group=audit&q=…&target=… — any of the
 * filter keys (q, target, admin, section, source, method, status, from, to)
 * replaces the filters, so the log shows exactly what the link names.
 */
const AuditPanel: React.FC<{ target?: AdminTargetIn }> = ({ target: link }) => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const q = useDebounced(f.q);
  const target = useDebounced(f.target);

  useEffect(() => {
    if (!link) return;
    const next: Filters = { ...EMPTY };
    (Object.keys(EMPTY) as (keyof Filters)[]).forEach(k => { next[k] = String(link[k] || '').trim(); });
    if (Object.values(next).some(Boolean)) { setF(next); setOpenId(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link?.nonce]);

  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));
  // A toggle: clicking the bar or KPI that is already filtering clears it.
  const toggle = (k: keyof Filters, v: string) => setF(s => ({ ...s, [k]: s[k] === v ? '' : v }));

  const filterParams = useMemo(() => ({
    q, admin: f.admin, source: f.source, section: f.section, method: f.method,
    status: f.status, target, from: f.from, to: f.to,
  }), [q, f.admin, f.source, f.section, f.method, f.status, target, f.from, f.to]);
  const filterKey = JSON.stringify(filterParams);

  // A new filter or sort starts again from the first page.
  useEffect(() => { setPage(0); }, [filterKey, perPage, sort]);

  const facets = useAdminGet('audit', 'facets', filterParams, [filterKey, tick]);
  const log = useAdminGet('audit', 'log',
    { ...filterParams, page, per_page: perPage, sort: sort?.key || '', dir: sort?.dir || '' },
    [filterKey, page, perPage, sort, tick]);

  const rows = useMemo(() => (log.data?.rows || []).map((r: any) => ({
    ...r,
    target: r.target_email || r.target_user_id || '',
  })), [log.data]);
  const total: number = log.data?.total ?? 0;
  const pages: number = log.data?.pages ?? 1;
  const kpis = facets.data?.kpis || {};
  const admins: string[] = (facets.data?.admins || []).map((a: any) => String(a.value));
  const sections: string[] = (facets.data?.sections || []).map((a: any) => String(a.value));
  const active = Object.entries(filterParams).filter(([, v]) => v).length;
  const today = iso(new Date());

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Actions today" value={(kpis.today ?? 0).toLocaleString()}
          onClick={() => setF(s => s.from === today && !s.to ? { ...s, from: '' } : { ...s, from: today, to: '' })}
          active={f.from === today && !f.to} hint="click to show them" />
        <KPI label="This week" value={(kpis.week ?? 0).toLocaleString()} hint="last 7 days" />
        <KPI label="Distinct admins" value={(kpis.admins ?? 0).toLocaleString()} hint="last 30 days, people only" />
        <KPI label="Failures" value={(kpis.failures ?? 0).toLocaleString()}
          onClick={() => toggle('status', 'failed')} active={f.status === 'failed'}
          hint="status ≥ 400, last 7 days" />
        <KPI label="All time" value={(kpis.all_time ?? 0).toLocaleString()} />
      </KPIRow>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="By admin">
          {facets.loading && !facets.data ? <Loading /> : (
            <BarList items={(facets.data?.admins || []).map((a: any) => ({ label: a.value || '(unknown)', value: a.n }))}
              onSelect={l => toggle('admin', l)} activeLabel={f.admin || undefined} />
          )}
        </Section>
        <Section title="By section">
          {facets.loading && !facets.data ? <Loading /> : (
            <BarList items={(facets.data?.sections || []).map((a: any) => ({ label: a.value || '(unknown)', value: a.n }))}
              onSelect={l => toggle('section', l)} activeLabel={f.section || undefined} />
          )}
        </Section>
      </div>

      <Section title="Log"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} entr{total === 1 ? 'y' : 'ies'}</span>
            {active > 0 && (
              <Btn small onClick={() => setF(EMPTY)}>Clear {active} filter{active === 1 ? '' : 's'}</Btn>
            )}
            <Btn small onClick={() => setTick(t => t + 1)}>Refresh</Btn>
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide
            placeholder="Search actor, action, path, target or payload…" />
          <Select value={f.admin} onChange={set('admin')}
            options={['', ...(f.admin && !admins.includes(f.admin) ? [f.admin] : []), ...admins]}
            labels={{ '': 'Any admin' }} />
          <Select value={f.source} onChange={set('source')} options={SOURCES} labels={SOURCE_LABELS} />
          <Select value={f.section} onChange={set('section')}
            options={['', ...(f.section && !sections.includes(f.section) ? [f.section] : []), ...sections]}
            labels={{ '': 'Any section' }} />
          <Select value={f.method} onChange={set('method')} options={METHODS} labels={{ '': 'Any method' }} />
          <Select value={f.status} onChange={set('status')} options={STATUSES} labels={STATUS_LABELS} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.target} onChange={set('target')} placeholder="Target email or user id" />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        <Note>
          Panel actions and raw admin routes are recorded by the API itself, with the verified
          actor — never a name the browser sent. System rows (the Razorpay webhook, the deletion
          sweep, stop-billing) have no admin behind them. Rows from before 2026-09-20 carry only
          actor, section, action and payload. Secrets are redacted before anything is stored.
        </Note>
        {log.error && <Msg kind="error">{log.error}</Msg>}
        {log.loading && !log.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} csvName="audit_log"
            serverSort={sort}
            onSortChange={(key, dir) => setSort(key && dir ? { key, dir } : null)}
            maxHeight="40rem"
            empty={active ? 'Nothing matches these filters.' : 'No audit entries yet.'}
            render={{
              created_at: r => <span title={r.created_at}>{fmtDate(r.created_at)}</span>,
              source: r => sourcePill(r.source),
              status: r => statusPill(r.status),
              duration_ms: r => r.duration_ms != null ? `${Number(r.duration_ms).toLocaleString()} ms` : '—',
              target: r => r.target ? (
                <button type="button" className="underline underline-offset-2 text-grayscale-1300"
                  title="Filter by this target" onClick={() => toggle('target', r.target)}>
                  {r.target}
                </button>
              ) : '—',
              summary: r => (
                <span className="text-xs text-grayscale-1200 font-mono" title={r.summary}>{r.summary || '—'}</span>
              ),
            }}
            actions={r => <Btn small onClick={() => setOpenId(Number(r.id))}>Details</Btn>} />
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
            <span>
              Page {page + 1} of {pages} · showing {rows.length ? page * Number(perPage) + 1 : 0}–
              {page * Number(perPage) + rows.length} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <Select value={perPage} onChange={setPerPage} options={['25', '50', '100', '200']}
                labels={{ '25': '25 / page', '50': '50 / page', '100': '100 / page', '200': '200 / page' }} />
              <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
              <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
              <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
              <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
            </div>
          </div>
        )}
      </Section>

      {openId !== null && <EntryDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
};

export default AuditPanel;
